// FakturowniaPaymentDao — data access for the two fakturownia payments
// read-model tables (fakturownia_payment + fakturownia_payment_allocation).
// All Supabase queries live here; the sync service and reconciler never
// hand-write queries (DAO-owns-queries rule). Plain class + constructor(sb),
// mirroring ShadowDao (no BaseDao / invalidation — these tables are synced,
// not user-mutated through Refine).

import type { SupabaseClient } from '../../base.ts';
import type {
  FaPairingBasis,
  FaPairingResolution,
  FaPairingStatus,
  FakturowniaPaymentAllocationResolution,
  FakturowniaPaymentAllocationRow,
  FakturowniaPaymentAllocationState,
  FakturowniaPaymentInsert,
  FakturowniaPaymentRow,
  FakturowniaPaymentState,
} from '@shared/types/fakturownia_payment.ts';
import type { InvoiceRow } from '@shared/types/invoice.ts';

const PAYMENTS_SYNC_CURSOR = 'fakturownia_payments_sync';

// Chunk sizes for the bulk batch methods. Reads keep the .in() list well under
// PostgREST's URL-length ceiling; writes stay small so one bad row can't sink a
// huge statement (and to bound the request body).
const BULK_READ_CHUNK = 200;

const BULK_WRITE_CHUNK = 100;

export class FakturowniaPaymentDao {
  constructor(private readonly sb: SupabaseClient) {}

  // ── fakturownia_payment ────────────────────────────────────────────────

  /**
   * Upsert a payment by its external id.
   *
   * `contentChanged` = the stored checksum differs from the incoming one (or the
   * row is new). last_synced_at is ALWAYS bumped (proves the row was seen this
   * sweep — deletion detection relies on it). When content is unchanged the prior
   * state + classified_checksum are preserved; when content changed the state is
   * reset to 'pending' (the reconciler must re-classify) while classified_checksum
   * is KEPT so the reconciler can compare old-vs-new.
   */
  async upsertPayment(
    row: FakturowniaPaymentInsert,
  ): Promise<{ row: FakturowniaPaymentRow; contentChanged: boolean }> {
    const existingRes = await this.sb
      .from('fakturownia_payment')
      .select('*')
      .eq('external_payment_id', row.external_payment_id)
      .maybeSingle();
    if (existingRes.error) throw existingRes.error;
    const prev = existingRes.data as FakturowniaPaymentRow | null;
    const contentChanged = prev == null || prev.checksum !== row.checksum;
    const now = new Date().toISOString();

    const payload: Record<string, unknown> = {
      external_payment_id: row.external_payment_id,
      origin: row.origin,
      amount: row.amount,
      currency: row.currency,
      paid_date: row.paid_date,
      name: row.name,
      kind: row.kind,
      settlement_class: row.settlement_class,
      remote_created_at: row.remote_created_at,
      remote_updated_at: row.remote_updated_at,
      remote_deleted_at: row.remote_deleted_at,
      content: row.content,
      checksum: row.checksum,
      last_synced_at: now,
    };

    if (prev == null) {
      payload.first_seen_at = now;
      payload.state = row.state ?? 'pending';
      payload.classified_checksum = row.classified_checksum ?? null;
    } else {
      payload.first_seen_at = prev.first_seen_at;
      // Reconciler compares classified_checksum against the live checksum, so we
      // keep the prior value even across a content change.
      payload.classified_checksum = prev.classified_checksum;
      payload.state = contentChanged ? 'pending' : prev.state;
    }

    const upsertRes = await this.sb
      .from('fakturownia_payment')
      .upsert(payload, { onConflict: 'external_payment_id' })
      .select('*')
      .single();
    if (upsertRes.error) throw upsertRes.error;
    return { row: upsertRes.data as FakturowniaPaymentRow, contentChanged };
  }

  /**
   * Payments that still need a reconciler pass: not remotely deleted AND
   * (classified_checksum differs from checksum OR state is still 'pending' OR at
   * least one allocation is still 'pending' OR a fa_only allocation now sits on an
   * invoice with an ADOPTABLE bank link — see faOnlyPaymentIdsWithAdoptableLink).
   * The column-to-column comparison and the pending-allocation EXISTS clause aren't
   * expressible as PostgREST filters, so remote_deleted_at IS NULL is pushed down
   * and the rest is applied in-app.
   */
  async listPaymentsNeedingClassification(limit = 200): Promise<FakturowniaPaymentRow[]> {
    // Re-open fa_only payments whose invoice gained a bank link after they were
    // classified (a match this method would otherwise never revisit — its own
    // predicate only fires on FA-side changes). Computed once for the whole walk.
    const relinkIds = await this.faOnlyPaymentIdsWithAdoptableLink();

    // Keyset page-walk over ALL non-deleted payments: an unranged select silently
    // hits PostgREST's ~1000-row cap, which starved classification on real volume
    // (2,189 payments → only the first 1000 ever reconciled). The needs-work
    // predicate mixes a column-to-column compare (classified_checksum vs checksum)
    // PostgREST can't express, so pages are filtered in-app; pending-allocation
    // existence is looked up per page in chunked .in() batches (same cap hazard).
    const PAGE = 1000;
    const CHUNK = 200;
    const out: FakturowniaPaymentRow[] = [];
    let cursor = '';
    for (;;) {
      let q = this.sb
        .from('fakturownia_payment')
        .select('*')
        .is('remote_deleted_at', null)
        .order('external_payment_id', { ascending: true })
        .limit(PAGE);
      if (cursor) q = q.gt('external_payment_id', cursor);
      const { data, error } = await q;
      if (error) throw error;
      const page = (data ?? []) as FakturowniaPaymentRow[];
      if (page.length === 0) break;
      cursor = page[page.length - 1].external_payment_id;

      const ids = page.map((p) => p.external_payment_id);
      const pendingSet = new Set<string>();
      for (let i = 0; i < ids.length; i += CHUNK) {
        const { data: pendingAllocs, error: aErr } = await this.sb
          .from('fakturownia_payment_allocation')
          .select('external_payment_id')
          .eq('state', 'pending')
          .in('external_payment_id', ids.slice(i, i + CHUNK));
        if (aErr) throw aErr;
        for (const r of (pendingAllocs ?? []) as { external_payment_id: string }[]) {
          pendingSet.add(r.external_payment_id);
        }
      }

      for (const p of page) {
        const needs =
          p.classified_checksum !== p.checksum ||
          p.state === 'pending' ||
          pendingSet.has(p.external_payment_id) ||
          relinkIds.has(p.external_payment_id);
        if (needs) out.push(p);
        if (out.length >= limit) return out;
      }
      if (page.length < PAGE) break;
    }
    return out;
  }

  /**
   * Payments the interim pairing pass (payments_invoice_linking=false) must
   * (re)process: live rows whose pairing_checksum lags the content checksum.
   * pairing_checksum vs checksum is a column-to-column compare PostgREST can't
   * express, so pages are keyset-walked (same ~1000-row PostgREST cap hazard as
   * {@link listPaymentsNeedingClassification}) and the predicate applied in-app;
   * remote_deleted_at IS NULL is pushed down to the query.
   */
  async listPaymentsNeedingPairing(limit = 200): Promise<FakturowniaPaymentRow[]> {
    const PAGE = 1000;
    const out: FakturowniaPaymentRow[] = [];
    let cursor = '';
    for (;;) {
      let q = this.sb
        .from('fakturownia_payment')
        .select('*')
        .is('remote_deleted_at', null)
        .order('external_payment_id', { ascending: true })
        .limit(PAGE);
      if (cursor) q = q.gt('external_payment_id', cursor);
      const { data, error } = await q;
      if (error) throw error;
      const page = (data ?? []) as FakturowniaPaymentRow[];
      if (page.length === 0) break;
      cursor = page[page.length - 1].external_payment_id;

      for (const p of page) {
        if (p.pairing_checksum !== p.checksum) {
          out.push(p);
          if (out.length >= limit) return out;
        }
      }
      if (page.length < PAGE) break;
    }
    return out;
  }

  /**
   * Live payments a sweep judged UNPAIRED for want of a bank line: pairing
   * status `no_line` (interim pairing sweep) and/or an allocation in
   * `conflict` / `fa_import_unpaired` (classification). Neither sweep's own
   * gate ever revisits them — both fire on Fakturownia-side change only — so a
   * statement uploaded later would never be noticed.
   * {@link PaymentReconciler.reopenArrivedLines} re-runs the pure pairing over
   * these and re-opens the ones whose line now exists. Keyset/range-paged like
   * the other walks (PostgREST ~1000-row cap).
   */
  async listUnpairedBankImportPayments(): Promise<
    Array<{ payment: FakturowniaPaymentRow; pairingNoLine: boolean; classificationUnpaired: boolean }>
  > {
    const PAGE = 1000;
    const byId = new Map<
      string,
      { payment: FakturowniaPaymentRow; pairingNoLine: boolean; classificationUnpaired: boolean }
    >();

    let cursor = '';
    for (;;) {
      let q = this.sb
        .from('fakturownia_payment')
        .select('*')
        .is('remote_deleted_at', null)
        .eq('pairing_status', 'no_line')
        .order('external_payment_id', { ascending: true })
        .limit(PAGE);
      if (cursor) q = q.gt('external_payment_id', cursor);
      const { data, error } = await q;
      if (error) throw error;
      const page = (data ?? []) as FakturowniaPaymentRow[];
      if (page.length === 0) break;
      cursor = page[page.length - 1].external_payment_id;
      for (const p of page) {
        byId.set(p.external_payment_id, { payment: p, pairingNoLine: true, classificationUnpaired: false });
      }
      if (page.length < PAGE) break;
    }

    const conflictIds = new Set<string>();
    let allocCursor = 0;
    for (;;) {
      let q = this.sb
        .from('fakturownia_payment_allocation')
        .select('id, external_payment_id')
        .eq('state', 'conflict')
        .eq('match_detail->>reason', 'fa_import_unpaired')
        .order('id', { ascending: true })
        .limit(PAGE);
      if (allocCursor) q = q.gt('id', allocCursor);
      const { data, error } = await q;
      if (error) throw error;
      const page = (data ?? []) as Array<{ id: number; external_payment_id: string }>;
      if (page.length === 0) break;
      allocCursor = page[page.length - 1].id;
      for (const r of page) conflictIds.add(r.external_payment_id);
      if (page.length < PAGE) break;
    }

    const missing: string[] = [];
    for (const id of conflictIds) {
      const known = byId.get(id);
      if (known) known.classificationUnpaired = true;
      else missing.push(id);
    }
    for (let i = 0; i < missing.length; i += BULK_READ_CHUNK) {
      const { data, error } = await this.sb
        .from('fakturownia_payment')
        .select('*')
        .is('remote_deleted_at', null)
        .in('external_payment_id', missing.slice(i, i + BULK_READ_CHUNK));
      if (error) throw error;
      for (const p of (data ?? []) as FakturowniaPaymentRow[]) {
        byId.set(p.external_payment_id, { payment: p, pairingNoLine: false, classificationUnpaired: true });
      }
    }
    return [...byId.values()];
  }

  /**
   * Re-open a payment for the sweeps named: `pairing` nulls `pairing_checksum`
   * (the interim pairing gate), `classification` nulls `classified_checksum`
   * (the classification gate). The verdict columns themselves are left for the
   * sweep to rewrite.
   */
  async reopenForSweeps(
    externalPaymentId: string,
    sweeps: { pairing: boolean; classification: boolean },
  ): Promise<void> {
    const update: Record<string, null> = {};
    if (sweeps.pairing) update.pairing_checksum = null;
    if (sweeps.classification) update.classified_checksum = null;
    if (Object.keys(update).length === 0) return;
    const { error } = await this.sb
      .from('fakturownia_payment')
      .update(update)
      .eq('external_payment_id', externalPaymentId);
    if (error) throw error;
  }

  /**
   * External-payment ids to re-open because a bank transfer is now waiting to be
   * adopted: every fa_only allocation, plus every fa-import echo (state 'echo'
   * with mbank_payment_link_id IS NULL — push-originated echoes carry their link
   * id and are naturally excluded), sitting on an invoice that currently has an
   * ADOPTABLE mbank_payment_link — unadopted (no fakturownia_payment_id) and not
   * already pushed/pushing, the exact predicate MbankPaymentLinkDao
   * .findUnadoptedByInvoices uses so the gate and the reconciler's adoption branch
   * agree on what "adoptable" means. Without this a bank line matched AFTER the FA
   * payment was classified never re-triggers classification, so the reconciler
   * never adopts it — for fa_only, recompute_invoice_payment_status
   * double-counts the bank link and the allocation; for a fa-import echo, the
   * pusher would later POST a duplicate payment to FA. Keyset-paged like the main
   * walk so a large unadopted backlog can't hit PostgREST's ~1000-row cap.
   */
  private async faOnlyPaymentIdsWithAdoptableLink(): Promise<Set<string>> {
    const PAGE = 1000;
    const invoiceIds = new Set<number>();
    // (statement_line_id, invoice_id) pairs that carry an adoptable link. The
    // reconciler adopts PAYMENT-level: a link on the echo's own paired line for
    // the same invoice, whatever the per-allocation rounding. The gate mirrors
    // exactly that, so it neither starves adoption nor re-selects rows whose
    // state can never change.
    const adoptableKeys = new Set<string>();
    let cursor = 0;
    for (;;) {
      let q = this.sb
        .from('mbank_payment_links')
        .select('id, invoice_id, statement_line_id, applied_in_invoice_ccy')
        .is('fakturownia_payment_id', null)
        .neq('fakturownia_push_status', 'pushed')
        .neq('fakturownia_push_status', 'pushing')
        .order('id', { ascending: true })
        .limit(PAGE);
      if (cursor) q = q.gt('id', cursor);
      const { data, error } = await q;
      if (error) throw error;
      const page = (data ?? []) as Array<{
        id: number;
        invoice_id: number | null;
        statement_line_id: number | null;
        applied_in_invoice_ccy: number | null;
      }>;
      if (page.length === 0) break;
      cursor = page[page.length - 1].id;
      for (const r of page) {
        if (r.invoice_id == null) continue;
        invoiceIds.add(r.invoice_id);
        if (r.statement_line_id == null || r.applied_in_invoice_ccy == null) continue;
        adoptableKeys.add(`${r.statement_line_id}:${r.invoice_id}`);
      }
      if (page.length < PAGE) break;
    }

    const out = new Set<string>();
    if (invoiceIds.size === 0) return out;
    const ids = [...invoiceIds];
    for (let i = 0; i < ids.length; i += BULK_READ_CHUNK) {
      const chunk = ids.slice(i, i + BULK_READ_CHUNK);
      // fa_only evidence (always link-less by construction — no guard needed).
      const faOnly = await this.sb
        .from('fakturownia_payment_allocation')
        .select('external_payment_id')
        .eq('state', 'fa_only')
        .in('invoice_id', chunk);
      if (faOnly.error) throw faOnly.error;
      // fa-import echoes: 'echo' with NO adopted link. Deliberately a SEPARATE
      // query (not `.in('state', …)` folded into the one above) so the fa_only
      // query's shape stays byte-identical — a locked regression test pins it.
      //
      // MUST CONVERGE: the reconciler can only adopt a link belonging to the
      // echo's OWN paired line (match_detail.statementLineId). Re-opening merely
      // because the INVOICE has some adoptable link re-selects rows whose state
      // can never change — observed on dev as 93 payments re-classified
      // identically every pass, forever. So the paired line is checked here.
      const echoes = await this.sb
        .from('fakturownia_payment_allocation')
        .select('external_payment_id, invoice_id, allocated_amount, match_detail')
        .eq('state', 'echo')
        .is('mbank_payment_link_id', null)
        .in('invoice_id', chunk);
      if (echoes.error) throw echoes.error;
      for (const r of (faOnly.data ?? []) as Array<{ external_payment_id: string }>) {
        out.add(r.external_payment_id);
      }
      for (const r of (echoes.data ?? []) as Array<{
        external_payment_id: string;
        invoice_id: number | null;
        allocated_amount: number | null;
        match_detail: { statementLineId?: unknown } | null;
      }>) {
        const lineId = r.match_detail?.statementLineId;
        if (typeof lineId !== 'number' || r.invoice_id == null) continue;
        if (adoptableKeys.has(`${lineId}:${r.invoice_id}`)) {
          out.add(r.external_payment_id);
        }
      }
    }
    return out;
  }

  /** Load a single payment row by its external id, or null when absent. Used by
   *  the resolution service to stamp a resolution's checksum from the LIVE payment
   *  (so a decision auto-invalidates once FA-side content changes). */
  async getPayment(externalPaymentId: string): Promise<FakturowniaPaymentRow | null> {
    const res = await this.sb
      .from('fakturownia_payment')
      .select('*')
      .eq('external_payment_id', externalPaymentId)
      .maybeSingle();
    if (res.error) throw res.error;
    return (res.data as FakturowniaPaymentRow | null) ?? null;
  }

  /** Set the classification verdict + the checksum it was computed against. */
  async markPaymentClassified(
    externalPaymentId: string,
    state: FakturowniaPaymentState,
    checksum: string,
  ): Promise<void> {
    const { error } = await this.sb
      .from('fakturownia_payment')
      .update({ state, classified_checksum: checksum })
      .eq('external_payment_id', externalPaymentId);
    if (error) throw error;
  }

  /**
   * Persist the interim pairing verdict for one payment (single .update() keyed by
   * external_payment_id). pairing_detail is ALWAYS written — defaults to null when
   * the caller omits it — so a re-pair that no longer needs candidates (e.g. an
   * `ambiguous` payment resolving to a single line on a later sweep) never leaves a
   * stale candidate blob behind.
   */
  async setPairing(
    externalPaymentId: string,
    fields: {
      paired_statement_line_id: number | null;
      pairing_status: FaPairingStatus;
      pairing_basis: FaPairingBasis | null;
      pairing_checksum: string;
      pairing_detail?: Record<string, unknown> | null;
    },
  ): Promise<void> {
    const { error } = await this.sb
      .from('fakturownia_payment')
      .update({
        paired_statement_line_id: fields.paired_statement_line_id,
        pairing_status: fields.pairing_status,
        pairing_basis: fields.pairing_basis,
        pairing_checksum: fields.pairing_checksum,
        pairing_detail: fields.pairing_detail ?? null,
      })
      .eq('external_payment_id', externalPaymentId);
    if (error) throw error;
  }

  /**
   * Write (or clear, by passing nulls) an operator's sticky pairing resolution.
   * The optional pairing_status / paired_statement_line_id / pairing_basis keys let
   * the caller reflect the resolution's effect on the pairing columns in the SAME
   * statement — mirrors {@link setAllocationResolution}'s "only write keys the
   * caller supplies" discipline so a resolution write never wipes a pairing field
   * it wasn't asked to touch.
   */
  async setPairingResolution(
    externalPaymentId: string,
    fields: {
      pairing_resolution: FaPairingResolution | null; // null clears
      pairing_resolved_by: string | null;
      pairing_resolved_at: string | null;
      pairing_resolution_checksum: string | null;
      pairing_status?: FaPairingStatus | null;
      paired_statement_line_id?: number | null;
      pairing_basis?: FaPairingBasis | null;
      /** `null` re-opens the interim sweep's gate so the next pairBatched
       *  pass reclassifies the payment from scratch (unpair). */
      pairing_checksum?: string | null;
    },
  ): Promise<void> {
    const update: Record<string, unknown> = {
      pairing_resolution: fields.pairing_resolution,
      pairing_resolved_by: fields.pairing_resolved_by,
      pairing_resolved_at: fields.pairing_resolved_at,
      pairing_resolution_checksum: fields.pairing_resolution_checksum,
    };
    if ('pairing_status' in fields) update.pairing_status = fields.pairing_status;
    if ('paired_statement_line_id' in fields) {
      update.paired_statement_line_id = fields.paired_statement_line_id;
    }
    if ('pairing_basis' in fields) update.pairing_basis = fields.pairing_basis;
    if ('pairing_checksum' in fields) update.pairing_checksum = fields.pairing_checksum;
    const { error } = await this.sb
      .from('fakturownia_payment')
      .update(update)
      .eq('external_payment_id', externalPaymentId);
    if (error) throw error;
  }

  /**
   * Detach every payment (live or tombstoned) paired to the given statement
   * lines and re-open the pairing sweep for it, so the next pass pairs it again
   * from scratch. Used before a duplicate statement line is removed. Returns the
   * external ids that were detached.
   */
  async unpairFromLines(statementLineIds: number[]): Promise<string[]> {
    const ids: string[] = [];
    for (let i = 0; i < statementLineIds.length; i += 200) {
      const { data, error } = await this.sb
        .from('fakturownia_payment')
        .update({ paired_statement_line_id: null, pairing_status: null, pairing_basis: null, pairing_checksum: null })
        .in('paired_statement_line_id', statementLineIds.slice(i, i + 200))
        .select('external_payment_id');
      if (error) throw error;
      ids.push(...((data ?? []) as Array<{ external_payment_id: string }>).map((r) => r.external_payment_id));
    }
    return ids;
  }

  /**
   * Live (non-deleted) payments currently paired to a given statement line —
   * the duplicate-line guard for the interim resolution service's `pairToLine`
   * (a bank line must not be claimed by two payments at once). Plain
   * single-column filter, no chunking needed (a line is claimed by at most a
   * handful of payments in practice).
   */
  async findLivePaymentsPairedToLine(statementLineId: number): Promise<FakturowniaPaymentRow[]> {
    const { data, error } = await this.sb
      .from('fakturownia_payment')
      .select('*')
      .eq('paired_statement_line_id', statementLineId)
      .is('remote_deleted_at', null);
    if (error) throw error;
    return (data ?? []) as FakturowniaPaymentRow[];
  }

  /**
   * Tombstone payments a COMPLETE full walk did not touch: not already deleted AND
   * last_synced_at strictly before the sweep start → remote_deleted_at = now().
   * ONLY safe to call after a full sweep finished without a page error — a partial
   * walk would tombstone rows that simply weren't on the fetched pages.
   */
  async markMissingAsDeleted(sweepStartedAtIso: string): Promise<number> {
    const { data, error } = await this.sb
      .from('fakturownia_payment')
      .update({ remote_deleted_at: new Date().toISOString() })
      .is('remote_deleted_at', null)
      .lt('last_synced_at', sweepStartedAtIso)
      .select('external_payment_id');
    if (error) throw error;
    return data?.length ?? 0;
  }

  /** Payments that look like echoes of our own bank pushes (used by echo-healing). */
  async findEchoCandidates(): Promise<FakturowniaPaymentRow[]> {
    const { data, error } = await this.sb
      .from('fakturownia_payment')
      .select('*')
      .or('origin.eq.mbank_push,name.ilike.*mbank:*');
    if (error) throw error;
    return (data ?? []) as FakturowniaPaymentRow[];
  }

  // ── fakturownia_payment_allocation ─────────────────────────────────────

  /**
   * Diff the allocation rows of a payment against the FA invoice-id set:
   * insert the newly-present ids (state='pending'), DELETE the rows whose
   * external_invoice_id is no longer present (a re-pin). The delete fires the DB
   * recompute trigger for the OLD invoice_id — this DAO never recomputes itself.
   */
  async syncAllocations(
    externalPaymentId: string,
    externalInvoiceIds: string[],
  ): Promise<{ added: number; removed: number }> {
    const { data, error } = await this.sb
      .from('fakturownia_payment_allocation')
      .select('id, external_invoice_id')
      .eq('external_payment_id', externalPaymentId);
    if (error) throw error;
    const current = (data ?? []) as { id: number; external_invoice_id: string }[];
    const currentIds = new Set(current.map((r) => r.external_invoice_id));
    const wanted = [...new Set(externalInvoiceIds)];
    const wantedSet = new Set(wanted);

    const toAdd = wanted.filter((id) => !currentIds.has(id));
    const toRemove = current.filter((r) => !wantedSet.has(r.external_invoice_id));

    if (toAdd.length > 0) {
      const rows = toAdd.map((external_invoice_id) => ({
        external_payment_id: externalPaymentId,
        external_invoice_id,
        state: 'pending' as FakturowniaPaymentAllocationState,
      }));
      const { error: insErr } = await this.sb
        .from('fakturownia_payment_allocation')
        .insert(rows);
      if (insErr) throw insErr;
    }
    if (toRemove.length > 0) {
      const { error: delErr } = await this.sb
        .from('fakturownia_payment_allocation')
        .delete()
        .in('id', toRemove.map((r) => r.id));
      if (delErr) throw delErr;
    }
    return { added: toAdd.length, removed: toRemove.length };
  }

  /**
   * Fill invoice_id on allocations that still carry a NULL FK by resolving
   * invoices.external_system_id (Fakturownia's invoice id) in a single batch.
   * Returns the number of allocation rows resolved.
   */
  async resolveInvoiceIds(limit = 500): Promise<number> {
    // Keyset pagination over ALL null-invoice allocations (id ascending): a fixed
    // first-page window would let a block of unresolvable rows (invoices not yet
    // synced) permanently shadow resolvable rows behind it — starvation during a
    // backload. `limit` is the page size, not a cap on total work.
    let cursor = 0;
    let totalResolved = 0;
    for (;;) {
      const { data, error } = await this.sb
        .from('fakturownia_payment_allocation')
        .select('id, external_invoice_id')
        .is('invoice_id', null)
        .gt('id', cursor)
        .order('id', { ascending: true })
        .limit(limit);
      if (error) throw error;
      const rows = (data ?? []) as { id: number; external_invoice_id: string }[];
      if (rows.length === 0) break;

      const externalIds = [...new Set(rows.map((r) => r.external_invoice_id))];
      const { data: invData, error: invErr } = await this.sb
        .from('invoices')
        .select('id, external_system_id')
        .in('external_system_id', externalIds);
      if (invErr) throw invErr;
      const byExternal = new Map<string, number>();
      for (const inv of (invData ?? []) as { id: number; external_system_id: string | null }[]) {
        if (inv.external_system_id) byExternal.set(inv.external_system_id, inv.id);
      }

      // Group allocation ids by resolved invoice id so re-pins collapse into one PATCH.
      const byInvoice = new Map<number, number[]>();
      for (const r of rows) {
        const invId = byExternal.get(r.external_invoice_id);
        if (invId == null) continue;
        totalResolved++;
        const arr = byInvoice.get(invId) ?? [];
        arr.push(r.id);
        byInvoice.set(invId, arr);
      }
      await Promise.all(
        [...byInvoice.entries()].map(async ([invId, allocIds]) => {
          const { error: uErr } = await this.sb
            .from('fakturownia_payment_allocation')
            .update({ invoice_id: invId })
            .in('id', allocIds);
          if (uErr) throw uErr;
        }),
      );

      cursor = rows[rows.length - 1].id;
      if (rows.length < limit) break;
    }
    return totalResolved;
  }

  /** Load a single allocation row by primary key, or null when absent. */
  async findAllocationById(id: number): Promise<FakturowniaPaymentAllocationRow | null> {
    const res = await this.sb
      .from('fakturownia_payment_allocation')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (res.error) throw res.error;
    return (res.data as FakturowniaPaymentAllocationRow | null) ?? null;
  }

  /** All allocation rows for a payment, stable order. */
  async listAllocations(externalPaymentId: string): Promise<FakturowniaPaymentAllocationRow[]> {
    const { data, error } = await this.sb
      .from('fakturownia_payment_allocation')
      .select('*')
      .eq('external_payment_id', externalPaymentId)
      .order('id', { ascending: true });
    if (error) throw error;
    return (data ?? []) as FakturowniaPaymentAllocationRow[];
  }

  /** Every allocation pinned to one invoice, with the payment header and the
   *  invoice's gross embedded — the /bank/payments details modal's evidence
   *  list ("which payments claim this invoice, and what does it total"). */
  async listAllocationsForInvoice(invoiceId: number): Promise<
    Array<
      FakturowniaPaymentAllocationRow & {
        payment: {
          amount: number | null;
          currency: string | null;
          paid_date: string | null;
          name: string | null;
          settlement_class: string | null;
          origin: string | null;
          remote_deleted_at: string | null;
        } | null;
        invoice: { gross_amount: number | null; currency: string | null } | null;
      }
    >
  > {
    const { data, error } = await this.sb
      .from('fakturownia_payment_allocation')
      .select(
        '*, payment:external_payment_id(amount,currency,paid_date,name,settlement_class,origin,remote_deleted_at), invoice:invoice_id(gross_amount,currency)',
      )
      .eq('invoice_id', invoiceId)
      .order('id', { ascending: true });
    if (error) throw error;
    return (data ?? []) as Array<
      FakturowniaPaymentAllocationRow & {
        payment: {
          amount: number | null;
          currency: string | null;
          paid_date: string | null;
          name: string | null;
          settlement_class: string | null;
          origin: string | null;
          remote_deleted_at: string | null;
        } | null;
        invoice: { gross_amount: number | null; currency: string | null } | null;
      }
    >;
  }

  /** listAllocations + the embedded invoice label (FE split editor rows). */
  async listAllocationsWithInvoice(
    externalPaymentId: string,
  ): Promise<Array<FakturowniaPaymentAllocationRow & { invoice: { id: number; invoice_number: string | null } | null }>> {
    const { data, error } = await this.sb
      .from('fakturownia_payment_allocation')
      .select('*, invoice:invoice_id(id,invoice_number)')
      .eq('external_payment_id', externalPaymentId)
      .order('id', { ascending: true });
    if (error) throw error;
    return (data ?? []) as Array<
      FakturowniaPaymentAllocationRow & { invoice: { id: number; invoice_number: string | null } | null }
    >;
  }

  /** Count of live orphan payments (state='orphan', not deleted) — queue badge. */
  async countOrphanPayments(): Promise<number> {
    const { count, error } = await this.sb
      .from('fakturownia_payment')
      .select('external_payment_id', { count: 'exact', head: true })
      .eq('state', 'orphan')
      .is('remote_deleted_at', null);
    if (error) throw error;
    return count ?? 0;
  }

  /**
   * Set an allocation's state, optionally amount / bank-link / match detail. Only
   * the keys the caller passes are written (a state-only update must not wipe an
   * amount set by an earlier pass). Fires the DB recompute trigger.
   */
  async setAllocationState(
    id: number,
    state: FakturowniaPaymentAllocationState,
    extras: {
      allocated_amount?: number | null;
      mbank_payment_link_id?: number | null;
      match_detail?: Record<string, unknown> | null;
    } = {},
  ): Promise<void> {
    const update: Record<string, unknown> = { state };
    if ('allocated_amount' in extras) update.allocated_amount = extras.allocated_amount ?? null;
    if ('mbank_payment_link_id' in extras) {
      update.mbank_payment_link_id = extras.mbank_payment_link_id ?? null;
    }
    if ('match_detail' in extras) update.match_detail = extras.match_detail ?? null;
    const { error } = await this.sb
      .from('fakturownia_payment_allocation')
      .update(update)
      .eq('id', id);
    if (error) throw error;
  }

  /**
   * Write an operator's sticky resolution onto an allocation: the verdict
   * (accepted / rejected / manual_link), who/when, the payment checksum it was
   * pinned to (so the reconciler auto-invalidates it if FA content changes), and
   * the resulting derived state. allocated_amount / mbank_payment_link_id /
   * match_detail are written only when the caller supplies the key — mirrors
   * setAllocationState so a resolution that doesn't touch the amount won't wipe
   * one set by an earlier pass. Fires the DB recompute trigger.
   */
  async setAllocationResolution(
    id: number,
    extras: {
      resolution: FakturowniaPaymentAllocationResolution;
      resolved_by: string;
      resolution_checksum: string;
      state: FakturowniaPaymentAllocationState;
      allocated_amount?: number | null;
      mbank_payment_link_id?: number | null;
      match_detail?: Record<string, unknown> | null;
    },
  ): Promise<void> {
    const update: Record<string, unknown> = {
      resolution: extras.resolution,
      resolved_by: extras.resolved_by,
      resolved_at: new Date().toISOString(),
      resolution_checksum: extras.resolution_checksum,
      state: extras.state,
    };
    if ('allocated_amount' in extras) update.allocated_amount = extras.allocated_amount ?? null;
    if ('mbank_payment_link_id' in extras) {
      update.mbank_payment_link_id = extras.mbank_payment_link_id ?? null;
    }
    if ('match_detail' in extras) update.match_detail = extras.match_detail ?? null;
    const { error } = await this.sb
      .from('fakturownia_payment_allocation')
      .update(update)
      .eq('id', id);
    if (error) throw error;
  }

  /**
   * Clear operator resolutions that were pinned to an OLD checksum: resolution set
   * AND resolution_checksum distinct from the current checksum (NULL counts as
   * distinct) → null out the resolution fields and reset state='pending'. A
   * resolution whose checksum still matches is sticky and left untouched.
   * Returns the number of allocations cleared.
   */
  async clearStaleResolutions(externalPaymentId: string, currentChecksum: string): Promise<number> {
    const { data, error } = await this.sb
      .from('fakturownia_payment_allocation')
      .update({
        resolution: null,
        resolved_by: null,
        resolved_at: null,
        resolution_checksum: null,
        state: 'pending',
      })
      .eq('external_payment_id', externalPaymentId)
      .not('resolution', 'is', null)
      .or(`resolution_checksum.neq.${currentChecksum},resolution_checksum.is.null`)
      .select('id');
    if (error) throw error;
    return data?.length ?? 0;
  }

  /**
   * Find an external FA payment on `invoiceId` we can adopt as the echo of a bank
   * push instead of POSTing a duplicate. Constraints: the payment has exactly ONE
   * allocation (single-invoice), is not remotely deleted, its allocation is in
   * ('pending','fa_only') with no bank link yet, and the amount matches within
   * `tolerance`. Oldest first (remote_created_at, falling back to first_seen_at).
   */
  async findAdoptableForInvoice(
    invoiceId: number,
    amount: number,
    tolerance = 0.01,
  ): Promise<{ payment: FakturowniaPaymentRow; allocation: FakturowniaPaymentAllocationRow } | null> {
    const { data: allocData, error: allocErr } = await this.sb
      .from('fakturownia_payment_allocation')
      .select('*')
      .eq('invoice_id', invoiceId)
      .in('state', ['pending', 'fa_only'])
      .is('mbank_payment_link_id', null);
    if (allocErr) throw allocErr;
    const allocs = (allocData ?? []) as FakturowniaPaymentAllocationRow[];
    if (allocs.length === 0) return null;

    const paymentIds = [...new Set(allocs.map((a) => a.external_payment_id))];

    // Total allocation count per payment (ALL invoices) — adopt only singletons.
    const { data: countData, error: countErr } = await this.sb
      .from('fakturownia_payment_allocation')
      .select('external_payment_id')
      .in('external_payment_id', paymentIds);
    if (countErr) throw countErr;
    const allocCount = new Map<string, number>();
    for (const r of (countData ?? []) as { external_payment_id: string }[]) {
      allocCount.set(r.external_payment_id, (allocCount.get(r.external_payment_id) ?? 0) + 1);
    }

    const { data: payData, error: payErr } = await this.sb
      .from('fakturownia_payment')
      .select('*')
      .in('external_payment_id', paymentIds)
      .is('remote_deleted_at', null);
    if (payErr) throw payErr;
    const payments = new Map<string, FakturowniaPaymentRow>();
    for (const p of (payData ?? []) as FakturowniaPaymentRow[]) {
      payments.set(p.external_payment_id, p);
    }

    const matches: { payment: FakturowniaPaymentRow; allocation: FakturowniaPaymentAllocationRow }[] = [];
    for (const a of allocs) {
      if ((allocCount.get(a.external_payment_id) ?? 0) !== 1) continue; // multi-allocation excluded
      const payment = payments.get(a.external_payment_id);
      if (!payment) continue; // deleted / missing
      const candidateAmount = a.allocated_amount ?? payment.amount;
      if (Math.abs(Math.abs(amount) - Math.abs(candidateAmount)) <= tolerance) {
        matches.push({ payment, allocation: a });
      }
    }
    if (matches.length === 0) return null;

    matches.sort((x, y) => {
      const xk = x.payment.remote_created_at ?? x.payment.first_seen_at;
      const yk = y.payment.remote_created_at ?? y.payment.first_seen_at;
      return xk < yk ? -1 : xk > yk ? 1 : 0;
    });
    return matches[0];
  }

  /**
   * Signed Σ allocated_amount of counting ('fa_only') allocations per invoice —
   * the persisted FA-external evidence the reconciler folds into its outstanding
   * and over-payment math so duplicate FA entries are caught ACROSS payments and
   * sweeps, not only within one payment's own allocations. Excludes allocations
   * of deleted payments and (optionally) of the payment currently being
   * re-classified, whose own stale allocations would double-count against the
   * run's fresh accumulator.
   */
  async sumFaOnlyByInvoices(
    invoiceIds: number[],
    excludeExternalPaymentId?: string,
  ): Promise<Map<number, number>> {
    const out = new Map<number, number>();
    if (invoiceIds.length === 0) return out;
    let q = this.sb
      .from('fakturownia_payment_allocation')
      .select('invoice_id, allocated_amount, external_payment_id, fakturownia_payment!inner(remote_deleted_at)')
      .eq('state', 'fa_only')
      .not('allocated_amount', 'is', null)
      .is('fakturownia_payment.remote_deleted_at', null)
      .in('invoice_id', invoiceIds);
    if (excludeExternalPaymentId != null) {
      q = q.neq('external_payment_id', excludeExternalPaymentId);
    }
    const { data, error } = await q;
    if (error) throw error;
    for (const r of (data ?? []) as Array<{ invoice_id: number | null; allocated_amount: number | null }>) {
      if (r.invoice_id == null || r.allocated_amount == null) continue;
      out.set(r.invoice_id, (out.get(r.invoice_id) ?? 0) + Number(r.allocated_amount));
    }
    return out;
  }

  // ── Bulk batch methods (kill the per-payment N+1) ──────────────────────
  //
  // These mirror the per-payment methods above but operate on many payments at
  // once. Reads chunk the .in() list at BULK_READ_CHUNK; writes chunk at
  // BULK_WRITE_CHUNK. Empty inputs short-circuit (an empty .in() would match
  // every row). Used by the batched sync path + batched reconciler; the
  // per-payment methods stay for the incremental/interactive callers.

  /**
   * Bulk-load payment rows by external id (chunked .in()). Rows with no match
   * are simply absent. Order is NOT guaranteed — the caller keys by id.
   */
  async getPaymentsByIds(ids: string[]): Promise<FakturowniaPaymentRow[]> {
    if (ids.length === 0) return [];
    const out: FakturowniaPaymentRow[] = [];
    for (let i = 0; i < ids.length; i += BULK_READ_CHUNK) {
      const chunk = ids.slice(i, i + BULK_READ_CHUNK);
      const { data, error } = await this.sb
        .from('fakturownia_payment')
        .select('*')
        .in('external_payment_id', chunk);
      if (error) throw error;
      out.push(...((data ?? []) as FakturowniaPaymentRow[]));
    }
    return out;
  }

  /**
   * Bulk upsert payment rows in ≤BULK_WRITE_CHUNK batches. The caller precomputes,
   * for each EXISTING row, the resolved {state, classified_checksum, first_seen_at}
   * exactly as {@link upsertPayment} does (state already folded for contentChanged),
   * and passes them in `preserve`. Rows whose id is absent from `preserve` are
   * treated as NEW: state = row.state ?? 'pending', classified_checksum =
   * row.classified_checksum ?? null, first_seen_at = now. last_synced_at is ALWAYS
   * bumped. onConflict = external_payment_id.
   */
  async bulkUpsertPayments(
    rows: FakturowniaPaymentInsert[],
    preserve: Map<
      string,
      { state: FakturowniaPaymentState; classified_checksum: string | null; first_seen_at: string }
    >,
  ): Promise<void> {
    if (rows.length === 0) return;
    const now = new Date().toISOString();
    const payloads = rows.map((row) => {
      const payload: Record<string, unknown> = {
        external_payment_id: row.external_payment_id,
        origin: row.origin,
        amount: row.amount,
        currency: row.currency,
        paid_date: row.paid_date,
        name: row.name,
        kind: row.kind,
        settlement_class: row.settlement_class,
        remote_created_at: row.remote_created_at,
        remote_updated_at: row.remote_updated_at,
        remote_deleted_at: row.remote_deleted_at,
        content: row.content,
        checksum: row.checksum,
        last_synced_at: now,
      };
      const prev = preserve.get(row.external_payment_id);
      if (prev == null) {
        payload.first_seen_at = now;
        payload.state = row.state ?? 'pending';
        payload.classified_checksum = row.classified_checksum ?? null;
      } else {
        payload.first_seen_at = prev.first_seen_at;
        payload.classified_checksum = prev.classified_checksum;
        payload.state = prev.state;
      }
      return payload;
    });
    for (let i = 0; i < payloads.length; i += BULK_WRITE_CHUNK) {
      const chunk = payloads.slice(i, i + BULK_WRITE_CHUNK);
      const { error } = await this.sb
        .from('fakturownia_payment')
        .upsert(chunk, { onConflict: 'external_payment_id' });
      if (error) throw error;
    }
  }

  /**
   * Bulk-load ALL allocation rows for many payments (chunked .in()), ordered by
   * id within each chunk. The caller groups by external_payment_id.
   */
  async listAllocationsByPaymentIds(ids: string[]): Promise<FakturowniaPaymentAllocationRow[]> {
    if (ids.length === 0) return [];
    const out: FakturowniaPaymentAllocationRow[] = [];
    for (let i = 0; i < ids.length; i += BULK_READ_CHUNK) {
      const chunk = ids.slice(i, i + BULK_READ_CHUNK);
      const { data, error } = await this.sb
        .from('fakturownia_payment_allocation')
        .select('*')
        .in('external_payment_id', chunk)
        .order('id', { ascending: true });
      if (error) throw error;
      out.push(...((data ?? []) as FakturowniaPaymentAllocationRow[]));
    }
    return out;
  }

  /** Bulk insert allocation rows (≤BULK_WRITE_CHUNK per statement). Fires the DB
   *  recompute trigger for each new row's invoice. */
  async bulkInsertAllocations(
    rows: Array<{
      external_payment_id: string;
      external_invoice_id: string;
      state: FakturowniaPaymentAllocationState;
    }>,
  ): Promise<void> {
    if (rows.length === 0) return;
    for (let i = 0; i < rows.length; i += BULK_WRITE_CHUNK) {
      const chunk = rows.slice(i, i + BULK_WRITE_CHUNK);
      const { error } = await this.sb.from('fakturownia_payment_allocation').insert(chunk);
      if (error) throw error;
    }
  }

  /** Bulk delete allocation rows by primary key (chunked .in()). Fires the DB
   *  recompute trigger for each removed row's OLD invoice. */
  async bulkDeleteAllocations(ids: number[]): Promise<void> {
    if (ids.length === 0) return;
    for (let i = 0; i < ids.length; i += BULK_WRITE_CHUNK) {
      const chunk = ids.slice(i, i + BULK_WRITE_CHUNK);
      const { error } = await this.sb
        .from('fakturownia_payment_allocation')
        .delete()
        .in('id', chunk);
      if (error) throw error;
    }
  }

  /**
   * Bulk-clear stale operator resolutions for many CHANGED payments in one pass.
   * Payments are grouped by their live checksum; each group runs ONE update
   * (external_payment_id IN group AND resolution IS NOT NULL AND resolution_checksum
   * differs from the group's checksum → reset to pending). Group ids chunked at
   * BULK_WRITE_CHUNK to bound the IN() list.
   *
   * Deliberately DOES NOT chain .select(): combining .or() + .update() + .select()
   * in one supabase-js call makes PostgREST re-apply the or-filter on read-back
   * (repo gotcha), which would silently mis-scope the write. The per-payment
   * {@link clearStaleResolutions} needs the returned count so it accepts that risk
   * on a single-payment filter; the batch path has no such need and returns void.
   */
  async bulkClearStaleResolutions(paymentIdToChecksum: Map<string, string>): Promise<void> {
    if (paymentIdToChecksum.size === 0) return;
    const byChecksum = new Map<string, string[]>();
    for (const [paymentId, checksum] of paymentIdToChecksum) {
      const arr = byChecksum.get(checksum) ?? [];
      arr.push(paymentId);
      byChecksum.set(checksum, arr);
    }
    for (const [checksum, paymentIds] of byChecksum) {
      for (let i = 0; i < paymentIds.length; i += BULK_WRITE_CHUNK) {
        const chunk = paymentIds.slice(i, i + BULK_WRITE_CHUNK);
        const { error } = await this.sb
          .from('fakturownia_payment_allocation')
          .update({
            resolution: null,
            resolved_by: null,
            resolved_at: null,
            resolution_checksum: null,
            state: 'pending',
          })
          .in('external_payment_id', chunk)
          .not('resolution', 'is', null)
          .or(`resolution_checksum.neq.${checksum},resolution_checksum.is.null`);
        if (error) throw error;
      }
    }
  }

  /**
   * Grouped fa_only evidence for the batched reconciler: per invoice, the signed
   * Σ allocated_amount of counting ('fa_only') allocations broken down BY
   * external_payment_id (deleted payments excluded, same join filter as
   * {@link sumFaOnlyByInvoices}). The reconciler subtracts a payment's own
   * contribution from the per-invoice total to reproduce
   * `sumFaOnlyByInvoices(ids, excludeExternalPaymentId)` WITHOUT one query per
   * payment. Chunked .in(); empty input short-circuits.
   */
  async sumFaOnlyGroupedByInvoices(invoiceIds: number[]): Promise<Map<number, Map<string, number>>> {
    const out = new Map<number, Map<string, number>>();
    if (invoiceIds.length === 0) return out;
    for (let i = 0; i < invoiceIds.length; i += BULK_READ_CHUNK) {
      const chunk = invoiceIds.slice(i, i + BULK_READ_CHUNK);
      const { data, error } = await this.sb
        .from('fakturownia_payment_allocation')
        .select(
          'invoice_id, allocated_amount, external_payment_id, fakturownia_payment!inner(remote_deleted_at)',
        )
        .eq('state', 'fa_only')
        .not('allocated_amount', 'is', null)
        .is('fakturownia_payment.remote_deleted_at', null)
        .in('invoice_id', chunk);
      if (error) throw error;
      for (const r of (data ?? []) as Array<{
        invoice_id: number | null;
        allocated_amount: number | null;
        external_payment_id: string;
      }>) {
        if (r.invoice_id == null || r.allocated_amount == null) continue;
        let byPayment = out.get(r.invoice_id);
        if (byPayment == null) {
          byPayment = new Map<string, number>();
          out.set(r.invoice_id, byPayment);
        }
        byPayment.set(
          r.external_payment_id,
          (byPayment.get(r.external_payment_id) ?? 0) + Number(r.allocated_amount),
        );
      }
    }
    return out;
  }

  /**
   * Bulk-load the invoice fields the payments reconciler needs (currency,
   * correction parent, gross, VAT-leg inputs), keyed by id (chunked .in()).
   *
   * Reads the `invoices` table directly rather than via InvoiceDao: the batched
   * reconciler needs ONE by-id fetch for a whole classification batch and InvoiceDao
   * (owned by another layer) exposes no chunked by-id loader. Mirrors
   * InvoiceDao.getByExternalIds' chunking. Missing ids are simply absent.
   */
  async getInvoicesByIds(ids: number[]): Promise<Map<number, InvoiceRow>> {
    const out = new Map<number, InvoiceRow>();
    if (ids.length === 0) return out;
    for (let i = 0; i < ids.length; i += BULK_READ_CHUNK) {
      const chunk = ids.slice(i, i + BULK_READ_CHUNK);
      const { data, error } = await this.sb.from('invoices').select('*').in('id', chunk);
      if (error) throw error;
      for (const inv of (data ?? []) as InvoiceRow[]) {
        out.set(inv.id, inv);
      }
    }
    return out;
  }

  // ── Sync cursor (reuses sync_metadata — same store as ShadowDao) ────────

  /** Cursor ISO timestamp for the payments sync (sync_metadata.sync_type). */
  async getCursor(): Promise<string | null> {
    const { data, error } = await this.sb
      .from('sync_metadata')
      .select('last_sync_at')
      .eq('sync_type', PAYMENTS_SYNC_CURSOR)
      .maybeSingle();
    if (error) throw error;
    return (data as { last_sync_at: string } | null)?.last_sync_at ?? null;
  }

  /** Advance (or initialize) the payments sync cursor. Upsert on sync_type. */
  async setCursor(iso: string): Promise<void> {
    const { error } = await this.sb
      .from('sync_metadata')
      .upsert(
        { sync_type: PAYMENTS_SYNC_CURSOR, last_sync_at: iso, updated_at: new Date().toISOString() },
        { onConflict: 'sync_type' },
      );
    if (error) throw error;
  }
}
